import type { ReactNode } from 'react';
import { Button, Tooltip } from '@heroui/react';
import { ButtonSpinner } from './ActionButton.tsx';

/**
 * A row action, as a glyph.
 *
 * ⚠️ THE LABEL IS REQUIRED AND IT NAMES THE ROW, not the glyph. «Изменить» on
 * its own is what a reader tabbing down a table of twenty hears twenty times,
 * with nothing to say which row is about to change — the same reasoning
 * `LootQueue`'s menu trigger already carries. Pass the subject:
 * `label="Изменить" subject={event.title}`.
 *
 * ⚠️ AND IT IS BOTH THE TOOLTIP AND THE ACCESSIBLE NAME. An icon nobody can
 * name is a puzzle; a tooltip that says something different from the
 * `aria-label` is two answers to one question. One string produces both.
 *
 * ⚠️ FILLED, NOT GHOST (2026-08-13, owner's call). A ghost button is invisible
 * until it is hovered, and hover is a thing a phone does not have — so on the
 * device this app is mostly used on, a row of row-actions was a row of floating
 * glyphs with no indication that any of them was a control. The fill is
 * `--default`, the same one every other resting control in the app carries.
 *
 * ⚠️ `tertiary`, NOT `secondary`, AND THE TWO ARE THE SAME FILL. They differ in
 * exactly one property: `.button--secondary` also sets `--button-fg` to
 * `--accent-soft-foreground`, which in the dark theme is the brand GOLD. Twenty
 * gold glyphs down a page read as twenty accent actions — gold is the link
 * colour here — and, worse, they drown the one glyph that is coloured on
 * purpose. `tertiary` inherits the foreground, so a row is neutral except for
 * the bin. Measured in both themes: glyph #FAF9F8 / #1C140E on #2A2622 /
 * #F0E8DC.
 *
 * ⚠️ `tone="danger"` STILL ONLY PAINTS THE GLYPH, and did not become
 * `variant="danger"` along with the fill. A permanently red BUTTON in a list of
 * twenty means nothing — it is decoration by the third row — and the red is
 * needed for the one state that matters: the ARMED second press, which is a
 * worded `danger` button and has to be the loudest thing in the row. The red on
 * the filled surface measures 3.99:1 in dark and 3.10:1 in light, both above
 * the 3:1 floor for a non-text control and both BETTER than the ghost version
 * they replace, which sat on the lighter card behind it.
 *
 * ⚠️ 36px SQUARE, WHICH IS SMALLER THAN THE 44px THUMB RULE AND IS THE POINT
 * OF THE CHANGE. These sit three-to-a-row inside a table cell on a 390px
 * screen; at 44 they would not fit, and the text buttons they replace were
 * 32px tall and merely wider. The rule holds for the app's primary controls —
 * the tab bar, the submit buttons — and a dense table's row actions are the
 * documented exception, exactly as the loot menu's 32px trigger already is.
 */
export function IconButton({
  label,
  subject,
  icon,
  onPress,
  isDisabled,
  isPending,
  tone,
}: {
  /** The verb. «Изменить», «Удалить». */
  label: string;
  /** What it acts on. Appended to the accessible name, absent from the tooltip. */
  subject?: string;
  icon: ReactNode;
  onPress: () => void;
  isDisabled?: boolean;
  /**
   * Whether THIS button is the one whose request is in flight.
   *
   * ⚠️ NOT THE SAME QUESTION AS `isDisabled`. While one row is saving, every
   * OTHER row is disabled and none of them is pending — showing a spinner in
   * all twenty would say twenty requests were running. See `useAction`.
   */
  isPending?: boolean;
  /** `danger` paints the glyph red — for a destructive action's RESTING state. */
  tone?: 'danger';
}) {
  return (
    <Tooltip delay={400}>
      <Button
        isIconOnly
        size="sm"
        variant="tertiary"
        isPending={isPending}
        /* `isPending` wins over `isDisabled` — see the note on `ActionButton`.
           Every call site passes `isDisabled={action.isBusy}`, which is true for
           the working row as well as the nineteen idle ones, and `disabled`
           would take the button out of the tab order at the moment react-aria
           announces that it started. */
        isDisabled={isPending === true ? false : isDisabled}
        aria-label={subject ? `${label} — ${subject}` : label}
        onPress={onPress}
        className={tone === 'danger' ? 'relative text-danger' : 'relative'}
      >
        {/* Hidden rather than replaced, so a spinning row action stays exactly
            36px wide and the row does not reflow around it. */}
        <span className={isPending ? 'invisible' : undefined}>{icon}</span>
        {isPending && <ButtonSpinner />}
      </Button>
      {/*
        The tooltip carries the VERB only. The subject is already on screen in
        the row the pointer is sitting in, and repeating it would make a
        hover-hint the longest thing in the cell.
      */}
      <Tooltip.Content>{label}</Tooltip.Content>
    </Tooltip>
  );
}
